import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../App";
import usePageTitle from "../hooks/usePageTitle";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  StickyNote, Plus, Search, Pin, PinOff, Trash2, Archive, ArchiveRestore,
  Share2, Lock, MoreVertical, Tag, X, Copy, Download, Loader2,
  Bold, Italic, Strikethrough, List, ListOrdered, Quote, Code,
  Heading1, Heading2, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon,
  Image as ImageIcon, Highlighter, Undo, Redo, CheckCircle, Circle, RotateCcw, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NOTE_COLORS = [
  { name: "White", value: "#ffffff" },
  { name: "Yellow", value: "#fef9c3" },
  { name: "Green", value: "#dcfce7" },
  { name: "Blue", value: "#dbeafe" },
  { name: "Purple", value: "#f3e8ff" },
  { name: "Pink", value: "#fce7f3" },
  { name: "Orange", value: "#ffedd5" },
  { name: "Gray", value: "#f3f4f6" },
];

// TipTap Editor Toolbar
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const addImage = () => {
    const url = window.prompt("Enter image URL:");
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const url = window.prompt("Enter link URL:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border-b border-slate-200 p-2 flex flex-wrap gap-1 bg-white sticky top-0 z-10">
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
        <Undo className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
        <Redo className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button variant={editor.isActive("heading", { level: 1 }) ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
        <Heading1 className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive("heading", { level: 2 }) ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button variant={editor.isActive("bold") ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive("italic") ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive("strike") ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive("highlight") ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleHighlight().run()}>
        <Highlighter className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button variant={editor.isActive("bulletList") ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive("orderedList") ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive("blockquote") ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive("codeBlock") ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
        <Code className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button variant={editor.isActive({ textAlign: "left" }) ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().setTextAlign("left").run()}>
        <AlignLeft className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive({ textAlign: "center" }) ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().setTextAlign("center").run()}>
        <AlignCenter className="w-4 h-4" />
      </Button>
      <Button variant={editor.isActive({ textAlign: "right" }) ? "default" : "ghost"} size="sm"
        onClick={() => editor.chain().focus().setTextAlign("right").run()}>
        <AlignRight className="w-4 h-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button variant="ghost" size="sm" onClick={addLink}>
        <LinkIcon className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={addImage}>
        <ImageIcon className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default function NotesPage() {
  usePageTitle("Notes");
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [activeTab, setActiveTab] = useState("active"); // active, completed, trash
  const [filterShared, setFilterShared] = useState(null);
  const [allTags, setAllTags] = useState([]);
  
  // Note counts for tabs
  const [noteCounts, setNoteCounts] = useState({ active: 0, completed: 0, trash: 0 });
  
  const [selectedNote, setSelectedNote] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editTags, setEditTags] = useState([]);
  const [editColor, setEditColor] = useState("#ffffff");
  const [newTagInput, setNewTagInput] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [showNewNoteDialog, setShowNewNoteDialog] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [showEmptyTrashDialog, setShowEmptyTrashDialog] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Link.configure({ openOnClick: false }),
      Image,
      Placeholder.configure({ placeholder: "Start writing your note..." }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: "",
    onUpdate: ({ editor }) => {
      setHasUnsavedChanges(true);
    },
  });

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (filterTag) params.append("tag", filterTag);
      params.append("status", activeTab);
      if (filterShared !== null) params.append("is_shared", filterShared);
      
      const response = await axios.get(`${BACKEND_URL}/api/notes?${params}`, { headers });
      setNotes(response.data.notes || []);
    } catch (error) {
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterTag, activeTab, filterShared]);

  // Fetch note counts for all tabs
  const fetchNoteCounts = useCallback(async () => {
    try {
      const [activeRes, completedRes, trashRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/notes?status=active`, { headers }),
        axios.get(`${BACKEND_URL}/api/notes?status=completed`, { headers }),
        axios.get(`${BACKEND_URL}/api/notes?status=trash`, { headers }),
      ]);
      setNoteCounts({
        active: activeRes.data.total || 0,
        completed: completedRes.data.total || 0,
        trash: trashRes.data.total || 0,
      });
    } catch (error) {
      console.error("Failed to fetch note counts:", error);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notes/tags`, { headers });
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  }, []);

  useEffect(() => {
    fetchNotes();
    fetchTags();
    fetchNoteCounts();
  }, [fetchNotes, fetchTags, fetchNoteCounts]);

  const selectNote = (note) => {
    if (hasUnsavedChanges && !window.confirm("You have unsaved changes. Discard them?")) return;
    setSelectedNote(note);
    setEditTitle(note.title);
    setEditTags(note.tags || []);
    setEditColor(note.color || "#ffffff");
    editor?.commands.setContent(note.content || "");
    setHasUnsavedChanges(false);
  };

  const createNote = async () => {
    if (!newNoteTitle.trim()) { toast.error("Please enter a title"); return; }
    try {
      setSaving(true);
      const response = await axios.post(`${BACKEND_URL}/api/notes`, {
        title: newNoteTitle, content: "", tags: [], is_shared: false, is_pinned: false, color: "#ffffff"
      }, { headers });
      toast.success("Note created");
      setShowNewNoteDialog(false);
      setNewNoteTitle("");
      fetchNotes();
      selectNote(response.data.note);
    } catch (error) {
      toast.error("Failed to create note");
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    if (!selectedNote) return;
    try {
      setSaving(true);
      await axios.put(`${BACKEND_URL}/api/notes/${selectedNote.id}`, {
        title: editTitle, content: editor?.getHTML() || "", tags: editTags, color: editColor
      }, { headers });
      toast.success("Note saved");
      setHasUnsavedChanges(false);
      fetchNotes();
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (note) => {
    try {
      await axios.put(`${BACKEND_URL}/api/notes/${note.id}`, { is_pinned: !note.is_pinned }, { headers });
      toast.success(note.is_pinned ? "Note unpinned" : "Note pinned");
      fetchNotes();
    } catch (error) {
      toast.error("Failed to update note");
    }
  };

  const toggleShare = async (note) => {
    try {
      await axios.put(`${BACKEND_URL}/api/notes/${note.id}`, { is_shared: !note.is_shared }, { headers });
      toast.success(note.is_shared ? "Note is now private" : "Note is now shared");
      fetchNotes();
      if (selectedNote?.id === note.id) setSelectedNote({ ...selectedNote, is_shared: !note.is_shared });
    } catch (error) {
      toast.error("Failed to update note");
    }
  };

  const deleteNote = async () => {
    if (!noteToDelete) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/notes/${noteToDelete.id}?permanent=${permanentDelete}`, { headers });
      toast.success(permanentDelete ? "Note permanently deleted" : "Note moved to trash");
      setShowDeleteDialog(false);
      setNoteToDelete(null);
      if (selectedNote?.id === noteToDelete.id) { setSelectedNote(null); editor?.commands.setContent(""); }
      fetchNotes();
      fetchNoteCounts();
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const restoreNote = async (note) => {
    try {
      await axios.post(`${BACKEND_URL}/api/notes/${note.id}/restore`, {}, { headers });
      toast.success("Note restored to Active");
      fetchNotes();
      fetchNoteCounts();
    } catch (error) {
      toast.error("Failed to restore note");
    }
  };

  const completeNote = async (note) => {
    try {
      await axios.post(`${BACKEND_URL}/api/notes/${note.id}/complete`, {}, { headers });
      toast.success("Note marked as completed");
      if (selectedNote?.id === note.id) { setSelectedNote(null); editor?.commands.setContent(""); }
      fetchNotes();
      fetchNoteCounts();
    } catch (error) {
      toast.error("Failed to complete note");
    }
  };

  const uncompleteNote = async (note) => {
    try {
      await axios.post(`${BACKEND_URL}/api/notes/${note.id}/uncomplete`, {}, { headers });
      toast.success("Note moved back to Active");
      fetchNotes();
      fetchNoteCounts();
    } catch (error) {
      toast.error("Failed to uncomplete note");
    }
  };

  const emptyTrash = async () => {
    try {
      const response = await axios.delete(`${BACKEND_URL}/api/notes/trash/empty`, { headers });
      toast.success(response.data.message || "Trash emptied");
      setShowEmptyTrashDialog(false);
      fetchNotes();
      fetchNoteCounts();
    } catch (error) {
      toast.error("Failed to empty trash");
    }
  };

  const duplicateNote = async (note) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/notes/${note.id}/duplicate`, {}, { headers });
      toast.success("Note duplicated");
      fetchNotes();
      selectNote(response.data.note);
    } catch (error) {
      toast.error("Failed to duplicate note");
    }
  };

  const exportAsText = (note) => {
    const content = note.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
    const blob = new Blob([`${note.title}\n\n${content}`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${note.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Note exported as text");
  };

  const addTag = () => {
    if (newTagInput.trim() && !editTags.includes(newTagInput.trim())) {
      setEditTags([...editTags, newTagInput.trim()]);
      setNewTagInput("");
      setHasUnsavedChanges(true);
    }
  };

  const removeTag = (tagToRemove) => {
    setEditTags(editTags.filter(t => t !== tagToRemove));
    setHasUnsavedChanges(true);
  };

  const isOwner = (note) => note.user_id === user?.id || note.created_by === user?.username;

  return (
    <div className="h-[calc(100vh-80px)] flex animate-fade-in">
      {/* Sidebar */}
      <div className="w-80 border-r border-slate-200 bg-slate-50 flex flex-col">
        <div className="p-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
              <StickyNote className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-foreground">Notepad</h1>
              <p className="text-xs text-muted-foreground">{notes.length} notes</p>
            </div>
          </div>
          <Button onClick={() => setShowNewNoteDialog(true)}
            className="w-full gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700">
            <Plus className="w-4 h-4" /> New Note
          </Button>
        </div>

        <div className="p-3 border-b border-slate-200 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search notes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 h-9" />
          </div>
          <div className="flex gap-2">
            <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}
              className="flex-1 h-8 text-xs border border-slate-300 rounded-md px-2">
              <option value="">All Tags</option>
              {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
            </select>
            <select value={filterShared === null ? "all" : filterShared ? "shared" : "personal"}
              onChange={(e) => setFilterShared(e.target.value === "all" ? null : e.target.value === "shared")}
              className="flex-1 h-8 text-xs border border-slate-300 rounded-md px-2">
              <option value="all">All Notes</option>
              <option value="personal">My Notes</option>
              <option value="shared">Shared</option>
            </select>
          </div>
          
          {/* Status Tabs */}
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
            <button
              onClick={() => setActiveTab("active")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                activeTab === "active" 
                  ? "bg-white text-amber-700 shadow-sm" 
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              <Circle className="w-3 h-3" />
              Active
              {noteCounts.active > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px]">
                  {noteCounts.active}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("completed")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                activeTab === "completed" 
                  ? "bg-white text-green-700 shadow-sm" 
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              <CheckCircle className="w-3 h-3" />
              Done
              {noteCounts.completed > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px]">
                  {noteCounts.completed}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("trash")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-all ${
                activeTab === "trash" 
                  ? "bg-white text-red-700 shadow-sm" 
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              <Trash2 className="w-3 h-3" />
              Trash
              {noteCounts.trash > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px]">
                  {noteCounts.trash}
                </span>
              )}
            </button>
          </div>
          
          {/* Empty Trash Button - only show in trash tab */}
          {activeTab === "trash" && noteCounts.trash > 0 && (
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => setShowEmptyTrashDialog(true)} 
              className="w-full h-8 text-xs"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Empty Trash ({noteCounts.trash} notes)
            </Button>
          )}
          
          {/* Auto-delete notice in trash */}
          {activeTab === "trash" && (
            <p className="text-[10px] text-slate-400 text-center">
              Notes in trash are automatically deleted after 30 days
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <StickyNote className="w-12 h-12 mx-auto mb-2 opacity-50" /><p className="text-sm">No notes found</p>
            </div>
          ) : (
            notes.map(note => (
              <Card key={note.id} onClick={() => selectNote(note)}
                className={`cursor-pointer transition-all hover:shadow-md ${selectedNote?.id === note.id ? 'ring-2 ring-amber-500 shadow-md' : ''} ${activeTab === 'completed' ? 'opacity-75' : ''}`}
                style={{ backgroundColor: note.color || '#ffffff' }}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {activeTab === 'completed' && <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />}
                        {activeTab === 'trash' && <Trash2 className="w-3 h-3 text-red-500 flex-shrink-0" />}
                        {activeTab === 'active' && note.is_pinned && <Pin className="w-3 h-3 text-amber-600 flex-shrink-0" />}
                        {note.is_shared ? <Share2 className="w-3 h-3 text-blue-500 flex-shrink-0" /> : <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                        <h3 className={`font-medium text-sm truncate ${activeTab === 'completed' ? 'line-through text-slate-500' : ''}`}>{note.title}</h3>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: note.content?.replace(/<[^>]*>/g, '').slice(0, 80) + '...' }} />
                      {note.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {note.tags.slice(0, 3).map(tag => <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">{tag}</Badge>)}
                          {note.tags.length > 3 && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">+{note.tags.length - 3}</Badge>}
                        </div>
                      )}
                      {/* Show deleted date for trash items */}
                      {activeTab === 'trash' && note.deleted_at && (
                        <p className="text-[10px] text-red-400 mt-1">
                          Deleted {formatDMY(note.deleted_at)}
                        </p>
                      )}
                      {/* Show completed date for completed items */}
                      {activeTab === 'completed' && note.completed_at && (
                        <p className="text-[10px] text-green-500 mt-1">
                          Completed {formatDMY(note.completed_at)}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <button className="p-1 hover:bg-slate-200 rounded"><MoreVertical className="w-4 h-4 text-slate-500" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        {/* Active tab actions */}
                        {activeTab === 'active' && isOwner(note) && (
                          <>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); completeNote(note); }} className="text-green-600">
                              <CheckCircle className="w-4 h-4 mr-2" /> Mark Complete
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); togglePin(note); }}>
                              {note.is_pinned ? <PinOff className="w-4 h-4 mr-2" /> : <Pin className="w-4 h-4 mr-2" />}
                              {note.is_pinned ? 'Unpin' : 'Pin'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); toggleShare(note); }}>
                              {note.is_shared ? <Lock className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                              {note.is_shared ? 'Make Private' : 'Share'}
                            </DropdownMenuItem>
                          </>
                        )}
                        
                        {/* Completed tab actions */}
                        {activeTab === 'completed' && isOwner(note) && (
                          <>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); uncompleteNote(note); }}>
                              <RotateCcw className="w-4 h-4 mr-2" /> Move to Active
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        
                        {/* Trash tab actions */}
                        {activeTab === 'trash' && isOwner(note) && (
                          <>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); restoreNote(note); }} className="text-green-600">
                              <RotateCcw className="w-4 h-4 mr-2" /> Restore
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        
                        {/* Common actions for active and completed */}
                        {activeTab !== 'trash' && (
                          <>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateNote(note); }}>
                              <Copy className="w-4 h-4 mr-2" /> Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportAsText(note); }}>
                              <Download className="w-4 h-4 mr-2" /> Export
                            </DropdownMenuItem>
                          </>
                        )}
                        
                        {/* Delete action */}
                        {isOwner(note) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setNoteToDelete(note); 
                                setPermanentDelete(activeTab === 'trash'); 
                                setShowDeleteDialog(true); 
                              }} 
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> 
                              {activeTab === 'trash' ? 'Delete Forever' : 'Move to Trash'}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-[10px] text-slate-400">
                    <span>{note.created_by}</span>
                    <span>{formatDMY(note.updated_at)}</span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedNote ? (
          <>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <Input value={editTitle} onChange={(e) => { setEditTitle(e.target.value); setHasUnsavedChanges(true); }}
                className="text-xl font-bold border-none shadow-none focus-visible:ring-0 px-0 flex-1"
                placeholder="Note title..." disabled={!isOwner(selectedNote)} />
              <div className="flex items-center gap-2">
                {isOwner(selectedNote) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="w-8 h-8 rounded-full border-2 border-slate-300 shadow-sm" style={{ backgroundColor: editColor }} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <div className="grid grid-cols-4 gap-1 p-2">
                        {NOTE_COLORS.map(color => (
                          <button key={color.value} onClick={() => { setEditColor(color.value); setHasUnsavedChanges(true); }}
                            className={`w-8 h-8 rounded-full border-2 ${editColor === color.value ? 'border-amber-500' : 'border-slate-200'}`}
                            style={{ backgroundColor: color.value }} title={color.name} />
                        ))}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {isOwner(selectedNote) && (
                  <Button variant="outline" size="sm" onClick={() => toggleShare(selectedNote)}>
                    {selectedNote.is_shared ? <><Share2 className="w-4 h-4 mr-1 text-blue-500" /> Shared</> : <><Lock className="w-4 h-4 mr-1" /> Private</>}
                  </Button>
                )}
                {isOwner(selectedNote) && (
                  <Button onClick={saveNote} disabled={saving || !hasUnsavedChanges} className="gap-2 bg-amber-500 hover:bg-amber-600">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {hasUnsavedChanges ? "Save Changes" : "Saved"}
                  </Button>
                )}
              </div>
            </div>

            <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-slate-400" />
              {editTags.map(tag => (
                <Badge key={tag} variant="secondary" className="gap-1">
                  {tag}
                  {isOwner(selectedNote) && <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeTag(tag)} />}
                </Badge>
              ))}
              {isOwner(selectedNote) && (
                <div className="flex items-center gap-1">
                  <Input value={newTagInput} onChange={(e) => setNewTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()} placeholder="Add tag..." className="h-7 w-24 text-xs" />
                  <Button size="sm" variant="ghost" onClick={addTag} className="h-7 px-2"><Plus className="w-3 h-3" /></Button>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-hidden flex flex-col" style={{ backgroundColor: editColor }}>
              {isOwner(selectedNote) && <EditorToolbar editor={editor} />}
              <div className="flex-1 overflow-y-auto">
                <EditorContent editor={editor} className="prose max-w-none p-6 min-h-full" />
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <StickyNote className="w-20 h-20 mb-4 opacity-50" />
            <h2 className="text-xl font-medium mb-2">Select a note</h2>
            <p className="text-sm mb-4">Choose a note from the sidebar or create a new one</p>
            <Button onClick={() => setShowNewNoteDialog(true)} className="gap-2"><Plus className="w-4 h-4" /> Create New Note</Button>
          </div>
        )}
      </div>

      <Dialog open={showNewNoteDialog} onOpenChange={setShowNewNoteDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create New Note</DialogTitle></DialogHeader>
          <div className="py-4">
            <Input value={newNoteTitle} onChange={(e) => setNewNoteTitle(e.target.value)}
              placeholder="Note title..." onKeyPress={(e) => e.key === 'Enter' && createNote()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewNoteDialog(false)}>Cancel</Button>
            <Button onClick={createNote} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-red-600">{permanentDelete ? "Delete Forever?" : "Move to Trash?"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{permanentDelete ? "This will permanently delete the note. This action cannot be undone." : "This will move the note to trash. You can restore it within 30 days."}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteNote}>{permanentDelete ? "Delete Forever" : "Move to Trash"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empty Trash Confirmation Dialog */}
      <Dialog open={showEmptyTrashDialog} onOpenChange={setShowEmptyTrashDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Empty Trash?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This will <strong>permanently delete {noteCounts.trash} note{noteCounts.trash !== 1 ? 's' : ''}</strong> from your trash.
            </p>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                This action cannot be undone!
              </p>
              <p className="text-xs text-red-600 mt-1">
                All notes will be permanently removed and cannot be recovered.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmptyTrashDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={emptyTrash} className="gap-2">
              <Trash2 className="w-4 h-4" />
              Empty Trash Forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        .ProseMirror { outline: none; min-height: 300px; padding: 0; }
        .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #94a3b8; pointer-events: none; height: 0; }
        .ProseMirror h1 { font-size: 2em; font-weight: 700; margin: 1em 0 0.5em; }
        .ProseMirror h2 { font-size: 1.5em; font-weight: 600; margin: 1em 0 0.5em; }
        .ProseMirror ul { padding-left: 1.5em; list-style-type: disc; margin: 0.5em 0; }
        .ProseMirror ol { padding-left: 1.5em; list-style-type: decimal; margin: 0.5em 0; }
        .ProseMirror ul ul { list-style-type: circle; }
        .ProseMirror ul ul ul { list-style-type: square; }
        .ProseMirror ol ol { list-style-type: lower-alpha; }
        .ProseMirror ol ol ol { list-style-type: lower-roman; }
        .ProseMirror li { margin: 0.25em 0; display: list-item; }
        .ProseMirror li p { margin: 0; display: inline; }
        .ProseMirror blockquote { border-left: 3px solid #e2e8f0; padding-left: 1em; color: #64748b; margin: 0.5em 0; }
        .ProseMirror code { background: #f1f5f9; padding: 0.2em 0.4em; border-radius: 4px; font-size: 0.9em; }
        .ProseMirror pre { background: #1e293b; color: #e2e8f0; padding: 1em; border-radius: 8px; overflow-x: auto; }
        .ProseMirror mark { background: #fef08a; padding: 0.1em 0.2em; }
        .ProseMirror a { color: #2563eb; text-decoration: underline; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 8px; margin: 1em 0; }
      `}</style>
    </div>
  );
}
